import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { Renderers } from "../render/prose";

/** `term-block`, in the dsh register — a block of code or terminal output.
 *
 * The CAPABILITY here is the native skin's, matched deliberately. That skin
 * mounts `std-code`, which is the layer with no editable branch in it at all
 * — it calls `createReadOnly` and that is the only constructor in the file —
 * so read-only stops being a value a component remembers to pass and becomes
 * a fact about the component it mounts. A second skin that rendered a bare
 * `pre` instead would be a second, quieter answer to "does this transcript
 * colour code", and the answer has to be one answer.
 *
 * The highlighter loads its plugin at runtime from the static host, and in a
 * sandboxed review card that fetch fails outright. So the bind's rejection is
 * CAUGHT rather than discarded and the text renders in a plain `pre` — a real
 * fallback rather than an apology, since this block's content is exactly what
 * a `pre` is for, the fallback cannot take a caret under any circumstances,
 * and the only thing lost is colour. Which also means a review card shows the
 * fallback, not the highlighter; that is worth saying up front, because what
 * a card can confirm here is the frame, the metrics and the fallback.
 *
 * What IS this skin's is the surface. dsh draws a code block as a soft card —
 * 12px of radius, a hairline, the hover fill rather than a sunken well, and a
 * roomier 12/16 inset than the native skin's 6/8 — and caps it at 224px so a
 * long paste scrolls INSIDE its own box instead of pushing the next row off
 * the screen. Both axes scroll in the frame: horizontal because code must not
 * be reflowed to fit, vertical because a transcript row is not a viewport. */
@component({ name: "std-dsh-term-block" })
export class DshTermBlock extends LonaWebComponent {
  /** The code renderer paints into this — std-code with its plain-text
   * understudy when the tento-ui renderers are installed (render/code.ts,
   * adapters/tento-ui.ts), a `<pre>` otherwise. Read-only on every path. */
  private $code: HTMLElement = this.$("code");

  static makeWith(props: DshTermBlock.Props): DshTermBlock {
    return (DshTermBlock.make() as DshTermBlock).bind(props);
  }

  /** Idempotent: the fallback text is SET, the fallback attribute is cleared
   * before the attempt, and the code view replaces its own document — so a
   * pooled row that fell back last time gets a fresh attempt this time. */
  bind(props: DshTermBlock.Props): this {
    // Not awaited — the transcript measures rows with a ResizeObserver, so a
    // block that fills a later frame re-reports its own height. The renderer
    // owns the highlighter's fallback (the plugin fetch is the failure this
    // block is most likely to meet; the seam's tento-ui renderer catches it
    // and shows a pre).
    void Renderers.code().render(this.$code, { text: props.text, language: props.language ?? "plain" });
    return this;
  }

  static $styles = [
    css`
      :host {
        display: block;
        width: 100%;
        min-width: 0;
      }
      /* The card. It is the same surface kv-grid draws, and that is the
         point: a block of output and a block of facts are one kind of object
         in this skin, so they share a radius, a hairline and a fill rather
         than each picking their own. */
      #frame {
        border: 1px solid var(--divider, rgba(127, 127, 127, 0.25));
        border-radius: 12px;
        background: var(--bg-hover, rgba(127, 127, 127, 0.06));
        /* A ceiling, not a height: a short block is its own size. Past this
           the block scrolls itself, which is what keeps the row from becoming
           the page's scroll container. */
        max-height: 224px;
        overflow: auto;
      }
      /* std-code brings its own box — a surface, an 8px radius and a block
         margin — because it is normally a standalone element in a document.
         Here #frame is already that box, so the inner one is undressed rather
         than nested inside it. An outer-tree rule is the one thing that
         outranks the component's own :host, which is what undressing needs. */
      #code {
        display: block;
        height: auto;
        margin-block: 0;
        border-radius: 0;
        background: transparent;
        --std-code-padding: 12px 16px;
        --font-size: var(--turn-fine-size, var(--text-8px));
      }
      /* Only ever visible when the highlighter could not load. Same metrics
         and the same inset as the block it replaces, so the frame is the same
         size whichever path runs. */
      #fallback {
        display: none;
        margin: 0;
        padding: 12px 16px;
        --font-size: var(--turn-fine-size, var(--text-8px));
        --line-height-ratio: 1.5;
        color: var(--text-sec);
        white-space: pre;
        overflow-x: auto;
      }
      :host([fallback]) #code {
        display: none;
      }
      :host([fallback]) #fallback {
        display: block;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <div id="frame">
      <div id="code"></div>
    </div>
  `;
}

export namespace DshTermBlock {
  /** std-code's own set, which is also the contract's. Anything it does not
   * recognise resolves to `plain`, so this is a promise about what will be
   * COLOURED rather than a validator — widening it past this list means
   * adding a language pack to the codemirror bundle. */
  export type Language = "plain" | "lisp" | "js" | "ts" | "rust";

  export type Props = {
    text: string;
    language?: Language;
  };
}

/** The long form of the name, for a caller that spells this slot's component
 * the way the turn family spells its own. One class, two export names — the
 * skin map may reach for either. */
export { DshTermBlock as DshTurnTermBlock };
